import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { H2, H3, P, UL, Note, TBD, LEGAL_ENTITY, PRODUCT, CONTACT_EMAIL } from './LegalLayout';

export default function Terms() {
  return (
    <LegalLayout
      title="Terms of Service"
      subtitle={`The agreement between ${LEGAL_ENTITY} and the medical practices that use ${PRODUCT}.`}
      updated="October 22, 2026"
    >
      <Note>
        These Terms govern use of {PRODUCT} by a <strong>practice</strong> and its staff. Patients do not create
        accounts and are not parties to this agreement — how patient health information is handled is described in the{' '}
        <Link to="/privacy" className="font-medium text-primary hover:underline">Privacy Policy</Link> and the{' '}
        <Link to="/hipaa" className="font-medium text-primary hover:underline">HIPAA notice</Link>.
      </Note>

      <H2 id="acceptance">1. Agreement to these Terms</H2>
      <P>
        These Terms of Service (the "<strong>Terms</strong>") are a binding agreement between {LEGAL_ENTITY},{' '}
        a Texas limited liability company doing business as DoctorPaperwork ("<strong>we</strong>", "<strong>us</strong>",
        "<strong>Adarga</strong>"), and the organization that registers for or uses {PRODUCT} (the
        "<strong>Practice</strong>", "<strong>you</strong>"). By creating an account, accepting an invitation to an
        existing account, or using the service, you agree to these Terms. If you are agreeing on behalf of an
        organization, you represent that you are authorized to bind it.
      </P>
      <P>
        If you do not agree, do not use the service. Where a signed order form, master services agreement, or Business
        Associate Agreement between us conflicts with these Terms, that signed document controls.
      </P>

      <H2 id="definitions">2. Definitions</H2>
      <UL>
        <li><strong>Service</strong> — the {PRODUCT} web application, APIs, and related services we make available to you.</li>
        <li><strong>Authorized User</strong> — an individual (physician, nurse, or administrator) whom you permit to access the Service under your account.</li>
        <li><strong>Seat</strong> — one Authorized User license, as counted for subscription billing.</li>
        <li><strong>Patient Data</strong> — information about your patients that is submitted to, generated in, or stored by the Service, including Protected Health Information.</li>
        <li><strong>PHI</strong> — Protected Health Information as defined at 45 C.F.R. § 160.103.</li>
        <li><strong>BAA</strong> — the Business Associate Agreement described in Section 7.</li>
        <li><strong>Document</strong> — a completed leave, disability, or accommodation form package prepared through the Service.</li>
      </UL>

      <H2 id="service">3. What the Service does — and does not do</H2>
      <P>
        The Service collects patient-reported information, assembles medical leave and accommodation paperwork
        (including FMLA certifications, short- and long-term disability forms, and ADA accommodation requests),
        produces AI-assisted draft content for clinical staff to review, and delivers the finished package after a
        licensed clinician reviews and signs it.
      </P>
      <P>
        When a patient uploads an employer's own form, the Service reads the questions it asks and, where it can,
        completes that form in its own boxes and returns it signed as part of the Document. This depends on what it is
        given: a flat, well-lit picture of each page, or the original PDF, is needed. Anything the Service cannot read
        or cannot place on the form is listed on one extra sheet delivered with the Document rather than entered on
        the form. The completed form is subject to the clinician review described below, and we do not warrant that
        an employer will accept it (Section 15).
      </P>
      <Note>
        <strong>The Service is documentation software. It does not practice medicine.</strong> It does not diagnose,
        treat, or recommend treatment, and it is not a clinical decision support tool, a medical device, or a
        substitute for professional judgment. Every clinical statement in a Document is the responsibility of the
        licensed clinician who signs it. Nothing the Service generates should be relied on without that review.
      </Note>
      <P>
        The Service is not a legal service and does not provide legal advice about family and medical leave, disability
        benefits, or employment law. Determinations of leave eligibility are made by employers, insurers, and
        administrators — not by us.
      </P>

      <H2 id="accounts">4. Accounts, seats, and security</H2>
      <UL>
        <li>You are responsible for all activity under your account, including that of every Authorized User.</li>
        <li>Credentials are personal to each Authorized User and must not be shared. Passwords must meet the Service's stated complexity requirements.</li>
        <li>We strongly recommend enabling multi-factor authentication for every Authorized User, and require it for administrator accounts where always — multi-factor authentication is required for every account.</li>
        <li>You must promptly deactivate Authorized Users who leave your organization or change roles. Administrators can do this in the Service at any time.</li>
        <li>You must notify us at <span className="font-medium text-foreground">{CONTACT_EMAIL}</span> without unreasonable delay after discovering any unauthorized access to your account.</li>
        <li>Seats are licensed, not transferable between organizations. Adding an Authorized User beyond your subscribed seat count may adjust your fees as described in Section 8.</li>
      </UL>

      <H2 id="responsibilities">5. Your responsibilities</H2>
      <P>You agree that:</P>
      <UL>
        <li>Every clinician who signs a Document through the Service holds a current, unrestricted license to practice in the jurisdiction where the care was provided, and has an established treatment relationship with the patient.</li>
        <li>You have obtained whatever patient consent or authorization applicable law requires before submitting Patient Data, and before disclosing a completed Document to an employer, insurer, or third-party administrator.</li>
        <li>The information you and your Authorized Users enter is accurate to the best of your knowledge, and you will not use the Service to create a record you know to be false.</li>
        <li>You will keep patient contact details current, since the Service delivers documents by link to the address or number on file.</li>
        <li>You will not use the Service to store or transmit PHI in fields not designated for it (for example, payment descriptions or practice display names).</li>
        <li>You will comply with applicable law in your use of the Service, including HIPAA, state medical records and privacy law, and — for text-message delivery — applicable telemarketing and messaging rules.</li>
      </UL>

      <H3>Prohibited uses</H3>
      <P>You will not, and will not permit anyone to:</P>
      <UL>
        <li>access the Service other than through the interfaces we provide, or attempt to bypass authentication, tenancy, or rate limits;</li>
        <li>probe, scan, or load-test the Service without our prior written consent (see Section 20 for coordinated disclosure);</li>
        <li>reverse engineer, decompile, or attempt to derive the source code of the Service, except as applicable law permits notwithstanding this restriction;</li>
        <li>resell, sublicense, or provide the Service to a third party as a service bureau, except to your own patients in the ordinary course;</li>
        <li>use the Service to develop a competing product, or use its outputs to train a machine learning model;</li>
        <li>upload malicious code, or content that infringes another party's rights.</li>
      </UL>

      <H2 id="ai">6. AI-assisted features</H2>
      <P>
        The Service uses large language models to draft narrative text, summarize patient answers, suggest form
        entries, and answer staff questions. These features are assistive only. Model output can be incomplete,
        inaccurate, or inconsistent with the underlying record, and you must not rely on it without review by a
        qualified member of your staff.
      </P>
      <P>
        Before any patient information is sent to a model, the Service removes HIPAA Safe Harbor identifiers — names,
        contact details, dates of birth, employer identity, and calendar dates, which are converted to durations. Our
        AI processing is performed by a subprocessor operating under a Business Associate Agreement with us. The
        de-identification layer and the current subprocessor are described in the{' '}
        <Link to="/privacy" className="font-medium text-primary hover:underline">Privacy Policy</Link>.
      </P>
      <P>
        We do not use your Patient Data to train our own models, and we do not permit our AI subprocessor to use it to
        train theirs.
      </P>

      <H2 id="baa">7. HIPAA and the Business Associate Agreement</H2>
      <P>
        In providing the Service we act as a <strong>business associate</strong> and you act as a{' '}
        <strong>covered entity</strong>, as those terms are used in HIPAA. A Business Associate Agreement is therefore
        required, and it is incorporated into these Terms by reference once executed. Our standard text is published
        at{' '}
        <Link to="/baa" className="font-medium text-primary hover:underline">Business Associate Agreement</Link> —
        publishing it is not executing it; see its Section 13.
      </P>
      <Note>
        <strong>You must execute a BAA with us before submitting real patient information to the Service.</strong>{' '}
        Demonstration and evaluation environments are provisioned with synthetic data for this reason. Submitting PHI
        before a BAA is in place is a breach of these Terms, and we may suspend the account under Section 10.
      </Note>
      <P>
        Where the BAA and these Terms conflict as to PHI, the BAA controls. Our obligations under the BAA — including
        breach notification, permitted uses and disclosures, and return or destruction of PHI on termination — are not
        limited or waived by any other provision of these Terms, including Section 15.
      </P>

      <H2 id="fees">8. Fees, billing, and patient charges</H2>
      <H3>Subscription</H3>
      <P>
        Practices pay a recurring, seat-based subscription in advance for each billing period. Fees, seat counts, and
        the billing period are those shown at checkout or on your order form. Payment is processed by our payment
        provider; we do not store full card numbers.
      </P>
      <H3>Per-document charges</H3>
      <P>
        Depending on the billing mode your practice selects, the fee for preparing a Document is either (a) paid by the
        patient directly through the Service, or (b) billed to the Practice as a fixed platform charge per Document,
        invoiced in arrears. The billable event in each case is the point at which a Document enters your staff review
        queue. Charges accrue per Document even if the Document is later withdrawn, revised, or never delivered,
        except where the failure is attributable to us.
      </P>
      <H3>Patient-paid mode</H3>
      <P>
        Where patients pay, you set the patient-facing price within the range the Service permits, and you are
        responsible for ensuring that the charge — and the way it is described to patients — complies with applicable
        law, your payer contracts, and any prohibition on charging for records or forms in your jurisdiction. We do
        not advise on this and disclaim responsibility for your pricing decisions.
      </P>
      <H3>Terms of payment</H3>
      <UL>
        <li>Fees are stated in U.S. dollars and are exclusive of taxes, which you are responsible for except for taxes on our net income.</li>
        <li>Invoices are due thirty (30) days from the invoice date. Overdue amounts may accrue interest at the lesser of 1% per month or the maximum rate permitted by law.</li>
        <li>Subscriptions renew automatically for successive periods unless cancelled before the renewal date.</li>
        <li>We may change fees on at least 30 days written notice, effective at your next renewal.</li>
        <li>Except where required by law or expressly stated, fees are non-refundable. Cancelling mid-period does not entitle you to a refund of the current period.</li>
        <li>Patient refund and dispute handling is described in the Service; chargebacks on patient payments may be passed through to you.</li>
      </UL>

      <H2 id="data">9. Your data</H2>
      <P>
        <strong>You own your Patient Data.</strong> We claim no ownership of it. You grant us a limited, non-exclusive
        license to host, process, transmit, and display it solely to provide and support the Service, to comply with
        law, and as the BAA otherwise permits.
      </P>
      <P>
        Signed Documents are immutable in the Service by design; corrections are handled through the revision request
        workflow rather than by editing a signed record. This is intentional and supports the integrity of the medical
        record.
      </P>
      <P>
        You may export your data at any time through the Service. On termination we will, at your written request made
        within 30 days, make your Patient Data available for export;
        after that period, and subject to the BAA and to legal retention obligations, we will return or destroy it.
      </P>
      <P>
        We may generate and use aggregated, de-identified statistics about use of the Service (for example, median
        turnaround time across all practices) to operate and improve it. Such data will not identify you, any
        Authorized User, or any patient, and will be de-identified consistent with 45 C.F.R. § 164.514.
      </P>

      <H2 id="term">10. Term, suspension, and termination</H2>
      <P>
        These Terms begin when you first accept them and continue while you have an active account. Either party may
        terminate for convenience effective at the end of the current billing period by giving notice through the
        Service or in writing.
      </P>
      <P>Either party may terminate immediately on written notice if the other materially breaches these Terms and fails to cure within 30 days of notice.</P>
      <P>We may suspend your access, in whole or in part, with notice where practicable, if:</P>
      <UL>
        <li>your account is used in a way that threatens the security, integrity, or availability of the Service or the confidentiality of PHI;</li>
        <li>fees are more than thirty (30) days past due;</li>
        <li>PHI is submitted without an executed BAA; or</li>
        <li>we are required to do so by law.</li>
      </UL>
      <P>Sections 9, 11, 13, 14, 15, 16, 17, and 21 survive termination, as do the BAA's surviving obligations.</P>

      <H2 id="ip">11. Intellectual property</H2>
      <P>
        We and our licensors own the Service, including its software, templates, form catalogs, designs, and
        trademarks. Subject to these Terms and payment of fees, we grant you a non-exclusive, non-transferable,
        revocable right to access and use the Service during the term for your internal clinical operations. No rights
        are granted except as expressly stated.
      </P>
      <P>
        If you send us suggestions or feedback, you grant us a perpetual, royalty-free license to use it without
        obligation to you. Feedback must not contain PHI.
      </P>

      <H2 id="esign">12. Electronic signatures and records</H2>
      <P>
        You and your Authorized Users consent to transact electronically. Documents signed through the Service are
        signed electronically, and you agree that such signatures are intended to be legally binding and are valid
        under the federal E-SIGN Act and applicable state UETA law. Each signature is recorded with the signer's
        identity, timestamp, and authentication context in an audit log.
      </P>
      <P>
        You are responsible for confirming that the recipients of your Documents — employers, insurers, and
        administrators — accept electronically signed forms. Some payers still require wet-ink or fax delivery.
      </P>

      <H2 id="third-party">13. Third-party services and delivery channels</H2>
      <P>
        The Service depends on third parties for hosting, storage, AI processing, payment, email, text messaging, and
        (where you enable it) fax. Those subprocessors are listed in the{' '}
        <Link to="/privacy" className="font-medium text-primary hover:underline">Privacy Policy</Link>. We remain
        responsible for our subprocessors' handling of PHI as the BAA provides, but we are not responsible for
        third-party services you connect independently.
      </P>
      <P>
        Email and text messages we send about a Document never contain PHI — they contain a secure link. Fax is the one
        channel that transmits the Document itself, so it is disabled unless a Business Associate Agreement with the
        fax vendor is in place. Delivery over any channel depends on carriers and recipients outside our control, and
        we do not guarantee that a particular transmission will arrive.
      </P>

      <H2 id="confidentiality">14. Confidentiality</H2>
      <P>
        Each party may receive non-public information of the other. The receiving party will use it only to perform
        under these Terms, protect it with at least reasonable care, and not disclose it except to personnel and
        advisors who need it and are bound by comparable obligations. This does not apply to information that is
        public, independently developed, or lawfully received from a third party, and does not prevent disclosure
        required by law with reasonable prior notice where permitted. PHI is governed by the BAA rather than this
        Section.
      </P>

      <H2 id="warranty">15. Disclaimers</H2>
      <P>
        We will provide the Service with reasonable skill and care. <strong>Except as expressly stated in these Terms
        or a signed agreement, the Service is provided "as is" and "as available", and we disclaim all other
        warranties, express or implied, including implied warranties of merchantability, fitness for a particular
        purpose, non-infringement, and any warranty arising from course of dealing.</strong>
      </P>
      <P>
        We do not warrant that the Service will be uninterrupted or error-free, that AI-generated content will be
        accurate, that a Document will be accepted by any employer, insurer, or administrator, or that use of the
        Service will result in approval of any leave, benefit, or accommodation. Some jurisdictions do not allow
        certain disclaimers, in which case they apply to the extent permitted.
      </P>

      <H2 id="liability">16. Limitation of liability</H2>
      <P>
        To the maximum extent permitted by law, neither party will be liable for indirect, incidental, special,
        consequential, exemplary, or punitive damages, or for lost profits, lost revenue, or loss of data or goodwill,
        even if advised of the possibility.
      </P>
      <P>
        Each party's total aggregate liability arising out of or relating to these Terms will not exceed the fees you
        paid or owed us for the Service in the twelve months preceding the event
        giving rise to the claim.
      </P>
      <Note>
        <strong>These limits do not apply to:</strong> our obligations under the BAA and HIPAA, including breach
        notification and the costs it imposes; either party's liability for death or personal injury caused by
        negligence; fraud or willful misconduct; your obligation to pay fees; or a party's indemnification
        obligations under Section 17 — except as <TBD>counsel to confirm which carve-outs are capped, and whether a
        super-cap applies to the PHI carve-out</TBD>.
      </Note>

      <H2 id="indemnity">17. Indemnification</H2>
      <P>
        <strong>By you.</strong> You will defend and indemnify us against third-party claims arising from (a) the
        clinical content of a Document signed by your clinicians, (b) your failure to obtain a required patient
        consent or authorization, (c) your pricing or billing of patients, or (d) your breach of Section 5.
      </P>
      <P>
        <strong>By us.</strong> We will defend and indemnify you against third-party claims that the Service, used as
        permitted, infringes a U.S. patent, copyright, or trade secret. We may modify the Service or terminate the
        affected functionality with a pro-rata refund if we cannot do so on commercially reasonable terms. This does
        not cover claims arising from your data or your combination of the Service with anything we did not provide.
      </P>
      <P>
        Indemnification is conditioned on prompt notice, sole control of the defense by the indemnifying party, and
        reasonable cooperation. No settlement imposing an obligation on the indemnified party may be made without its
        consent.
      </P>

      <H2 id="changes">18. Changes to the Service and to these Terms</H2>
      <P>
        We develop the Service continuously and may change features. We will not materially reduce the core
        functionality you subscribe to during a paid period without notice.
      </P>
      <P>
        We may update these Terms. For material changes we will give at least 30 days{' '}
        notice by email to your account administrators and by posting the updated Terms here. Changes take effect at
        the start of your next billing period; continuing to use the Service after that date means you accept them. If
        you object, you may terminate before the effective date.
      </P>

      <H2 id="law">19. Governing law and disputes</H2>
      <P>
        These Terms are governed by the laws of the State of Texas, excluding its conflict-of-laws rules and
        the U.N. Convention on Contracts for the International Sale of Goods. The parties submit to the exclusive
        jurisdiction of the state and federal courts located in the state and federal courts located in Bexar County, Texas.
      </P>
      <P>
        <TBD>Counsel to decide: mandatory arbitration and class-action waiver, or courts only. Note that a
        business-to-business agreement between a vendor and medical practices raises different considerations than a
        consumer agreement, and that patients are not parties to these Terms.</TBD>
      </P>
      <P>
        Before filing any claim, the parties will attempt in good faith to resolve the dispute through discussion
        between representatives with authority to settle, for at least thirty (30) days after
        written notice of the dispute.
      </P>

      <H2 id="general">20. Security reporting</H2>
      <P>
        If you believe you have found a security vulnerability in the Service, report it to{' '}
        <span className="font-medium text-foreground">security@adargamedical.com</span> rather than testing it further.
        We will acknowledge reports promptly and will not pursue action against good-faith researchers who report
        privately, avoid privacy violations and service degradation, and give us reasonable time to remediate.
      </P>

      <H2 id="misc">21. General</H2>
      <UL>
        <li><strong>Entire agreement.</strong> These Terms, together with any signed order form and the BAA, are the entire agreement on this subject and supersede prior discussions.</li>
        <li><strong>Assignment.</strong> Neither party may assign these Terms without the other's consent, except to a successor in a merger or sale of substantially all assets, with notice.</li>
        <li><strong>No waiver.</strong> A failure to enforce a provision is not a waiver of it.</li>
        <li><strong>Severability.</strong> If a provision is unenforceable, it is modified to the minimum extent necessary and the rest remains in force.</li>
        <li><strong>Force majeure.</strong> Neither party is liable for delay or failure caused by events beyond its reasonable control, excluding payment obligations.</li>
        <li><strong>Independent parties.</strong> Nothing here creates a partnership, joint venture, employment, or agency relationship, and neither party may bind the other.</li>
        <li><strong>Notices.</strong> Notices to us go to 530 Berwick Town, San Antonio, TX 78249 and <span className="font-medium text-foreground">{CONTACT_EMAIL}</span>; notices to you go to your account administrators' email addresses on file.</li>
        <li><strong>Third-party beneficiaries.</strong> There are none, except as the BAA provides.</li>
      </UL>

      <H2 id="contact">22. Contact</H2>
      <P>
        {LEGAL_ENTITY} · 530 Berwick Town, San Antonio, TX 78249 ·{' '}
        <span className="font-medium text-foreground">{CONTACT_EMAIL}</span>
      </P>
    </LegalLayout>
  );
}
